import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";

const TONE_ICON = {
  destructive: {
    wrapper: "bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400",
    Icon: Trash2,
  },
  warning: {
    wrapper: "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400",
    Icon: AlertTriangle,
  },
};

const CONFIRM_BUTTON_CLASS = {
  destructive: "bg-red-600 hover:bg-red-700 text-white border-none focus:ring-red-500",
  primary:
    "bg-[#0F6E56] hover:bg-[#1D9E75] dark:bg-emerald-600 dark:hover:bg-emerald-500 text-white border-none",
};

export function buildDeleteDescription(itemName, fallbackLabel = "this item") {
  const label = itemName || fallbackLabel;

  return (
    <>
      Are you sure you want to delete{" "}
      <span className="font-semibold text-gray-900 dark:text-gray-100">&quot;{label}&quot;</span>
      ? This action is permanent and cannot be undone.
    </>
  );
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  loadingLabel,
  tone = "destructive",
  confirmVariant = "destructive",
  blockDismissWhileLoading = true,
}) {
  const { wrapper, Icon } = TONE_ICON[tone] || TONE_ICON.destructive;
  const resolvedLoadingLabel = loadingLabel || `${confirmLabel}…`;

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && blockDismissWhileLoading && loading) return;
    onOpenChange?.(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800"
        onPointerDownOutside={(event) => {
          if (blockDismissWhileLoading && loading) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="flex flex-col items-center sm:items-start text-center sm:text-left">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${wrapper}`}
          >
            <Icon className="w-6 h-6" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-4 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="w-full sm:w-auto border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`w-full sm:w-auto ${CONFIRM_BUTTON_CLASS[confirmVariant] || CONFIRM_BUTTON_CLASS.destructive}`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {resolvedLoadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
